import { z } from "zod";

/**
 * Product catalogue rules shared by the cart, checkout and the API.
 * Product documents (name, price, …) live in Firestore `products/{id}`.
 */

export const ProductSchema = z.object({
  id: z.string(),
  name: z.string().catch(""),
  price: z.number().catch(0),
  imageTransform: z
    .union([z.string(), z.object({ portrait: z.string(), landscape: z.string() })])
    .catch(""),
  imageTransformLogo: z.string().optional().catch(undefined),
  size: z.array(z.string()).optional().catch(undefined),
  description: z.string().optional().catch(undefined),
  order: z.coerce.number().catch(0),
});
export type Product = z.infer<typeof ProductSchema>;

/** Parse a `products/{id}` document (client or admin SDK data). Never throws. */
export function parseProduct(id: string, data: unknown): Product {
  return ProductSchema.parse({ ...(data as object), id });
}

// ─── Delivery ────────────────────────────────────────────────────────────────

/**
 * Standard delivery price for physical products (EasyBox / livrare).
 * Used in checkout UI and in Stripe session (as amount in cents).
 */
export const DELIVERY_PRICE_RON =
  typeof process !== "undefined" && process.env.NEXT_PUBLIC_DELIVERY_PRICE_RON != null
    ? Number(process.env.NEXT_PUBLIC_DELIVERY_PRICE_RON)
    : 20;

/** Delivery price in cents (for Stripe). */
export const DELIVERY_PRICE_CENTS = DELIVERY_PRICE_RON * 100;

/** Minimum order amount (RON) when cart has physical products (delivery required). */
export const MIN_ORDER_WITH_DELIVERY_RON =
  typeof process !== "undefined" && process.env.NEXT_PUBLIC_MIN_ORDER_DELIVERY_RON != null
    ? Number(process.env.NEXT_PUBLIC_MIN_ORDER_DELIVERY_RON)
    : 50;

// ─── Product ids ─────────────────────────────────────────────────────────────

/** The digital download of a photo: max quantity 1, never shipped. */
export const DIGITAL_PICTURE_PRODUCT_ID = "picture-digital";

/** SmartBill product code ("cod produs") per product id. */
const PRODUCT_CODES: Record<string, string> = {
  [DIGITAL_PICTURE_PRODUCT_ID]: "FOTO",
  "picture-s": "PRINT",
  mug: "CANA",
  tshirt: "TRICOU",
};

export function productCode(productId: string): string {
  return PRODUCT_CODES[productId] ?? "FOTO";
}

/** Everything except the digital photo is a physical product that needs delivery. */
export function isPhysicalProduct(productId: string | undefined): boolean {
  return productId !== DIGITAL_PICTURE_PRODUCT_ID;
}

/** Quantity the cart allows for a product (the digital photo is capped at 1). */
export function clampQuantity(productId: string, qty: number): number {
  return productId === DIGITAL_PICTURE_PRODUCT_ID ? 1 : qty;
}
